import { useEffect, useState } from "react";
import { api, formatErr } from "@/lib/api";
import { toast } from "sonner";
import { AlertTriangle, Trash2, Power, Star, Sparkles, Gavel, Clock, Play, StopCircle, Wrench } from "lucide-react";

function DangerButton({ onClick, label, testid, busy }) {
  return (
    <button
      onClick={onClick}
      disabled={busy}
      data-testid={testid}
      className="inline-flex items-center gap-2 px-5 py-3 border border-red-700/60 bg-red-950/40 text-red-300 hover:bg-red-900/40 hover:text-red-200 transition-colors disabled:opacity-50"
    >
      <Trash2 size={14} />
      {busy ? "Working…" : label}
    </button>
  );
}

function ConfirmCard({ title, body, onCancel, onConfirm, confirmLabel = "Yes, delete everything", busy, testid }) {
  return (
    <div className="bg-red-950/30 border border-red-700/60 p-5 mt-4 space-y-3" data-testid={testid}>
      <div className="flex items-start gap-3">
        <AlertTriangle className="text-red-400 flex-shrink-0 mt-0.5" size={18} />
        <div>
          <p className="text-red-200 font-medium mb-1">{title}</p>
          <p className="text-red-300/80 text-sm leading-relaxed">{body}</p>
        </div>
      </div>
      <div className="flex gap-3 pt-2">
        <button
          onClick={onCancel}
          disabled={busy}
          className="px-4 py-2 border border-[#2A2A2A] text-[#A3A3A3] hover:text-[#F5F5DC] text-sm uppercase tracking-widest"
          data-testid={`${testid}-cancel`}
        >
          Cancel
        </button>
        <button
          onClick={onConfirm}
          disabled={busy}
          className="px-4 py-2 bg-red-700 hover:bg-red-600 text-white text-sm uppercase tracking-widest disabled:opacity-50"
          data-testid={`${testid}-confirm`}
        >
          {busy ? "Wiping…" : confirmLabel}
        </button>
      </div>
    </div>
  );
}

export default function AdminSettings() {
  const [paused, setPaused] = useState(false);
  const [freeDelivery, setFreeDelivery] = useState(false);
  const [announcement, setAnnouncement] = useState("");
  const [saving, setSaving] = useState(false);
  const [confirm, setConfirm] = useState(null); // 'products' | 'orders' | null
  const [resetBusy, setResetBusy] = useState(false);
  const [catSpecials, setCatSpecials] = useState([]);
  const [togglingCat, setTogglingCat] = useState(null);
  const [repairBusy, setRepairBusy] = useState(false);
  const [repairResult, setRepairResult] = useState(null); // { summary, products } | { error }
  const [labPaused, setLabPaused] = useState(false);
  const [labToggling, setLabToggling] = useState(false);
  const [auctions, setAuctions] = useState([]);
  const [products, setProducts] = useState([]);
  const [auctionProductId, setAuctionProductId] = useState("");
  const [auctionStartingBid, setAuctionStartingBid] = useState("");
  const [auctionBuyNow, setAuctionBuyNow] = useState("");
  const [auctionDescription, setAuctionDescription] = useState("");
  const [auctionBusy, setAuctionBusy] = useState(false);

  const CATEGORY_LABELS = {
    chopping_board: "Chopping Boards",
    resin_platter: "Resin Platters",
    furniture: "Furniture",
    miscellaneous: "Miscellaneous",
  };

  const loadCatSpecials = () =>
    api.get("/admin/specials/status")
      .then((r) => setCatSpecials(r.data))
      .catch(() => {});

  useEffect(() => {
    api.get("/settings").then((r) => {
      setPaused(!!r.data.paused);
      setAnnouncement(r.data.announcement || "");
    });
    api.get("/settings/resin-lab-status")
      .then((r) => setLabPaused(!!r.data?.paused))
      .catch(() => {});
    loadCatSpecials();
    loadAuctions();
    api.get("/admin/products").then((r) => setProducts(r.data)).catch(() => {});
  }, []);

  const loadAuctions = () =>
    api.get("/admin/auctions").then((r) => setAuctions(r.data)).catch(() => {});

  const startAuction = async () => {
    if (auctionBusy || !auctionProductId) return;
    setAuctionBusy(true);
    try {
      const payload = { product_id: auctionProductId };
      const sb = parseFloat(auctionStartingBid);
      if (auctionStartingBid.trim() && !Number.isNaN(sb) && sb > 0) payload.starting_bid = sb;
      const bn = parseFloat(auctionBuyNow);
      if (auctionBuyNow.trim() && !Number.isNaN(bn) && bn > 0) payload.buy_now_price = bn;
      if (auctionDescription.trim()) payload.auction_description = auctionDescription.trim();
      await api.post("/admin/auctions/start", payload);
      toast.success("15-day auction started");
      setAuctionProductId("");
      setAuctionStartingBid("");
      setAuctionBuyNow("");
      setAuctionDescription("");
      await loadAuctions();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setAuctionBusy(false); }
  };

  const endAuction = async (id) => {
    if (auctionBusy) return;
    setAuctionBusy(true);
    try {
      await api.post(`/admin/auctions/${id}/end`, {});
      toast.success("Auction ended — winner emailed");
      await loadAuctions();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setAuctionBusy(false); }
  };

  const formatTimeLeft = (endIso) => {
    const ms = new Date(endIso).getTime() - Date.now();
    if (ms <= 0) return "Ended";
    const d = Math.floor(ms / 86400000);
    const h = Math.floor((ms % 86400000) / 3600000);
    const m = Math.floor((ms % 3600000) / 60000);
    return `${d}d ${h}h ${m}m`;
  };

  const toggleResinLab = async () => {
    if (labToggling) return;
    const next = !labPaused;
    setLabToggling(true);
    try {
      await api.post("/admin/settings/resin-lab", { paused: next });
      setLabPaused(next);
      toast.success(next ? "Resin Lab paused — new orders blocked" : "Resin Lab is now accepting orders");
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setLabToggling(false);
    }
  };

  const runSpecialsRepair = async () => {
    if (repairBusy) return;
    setRepairBusy(true);
    setRepairResult(null);
    try {
      const { data } = await api.post("/admin/specials/repair");
      setRepairResult(data);
      const s = data.summary || {};
      const fixed = s.fixed || 0;
      const review = s.needs_review || 0;
      if (fixed > 0) {
        toast.success(`Repair complete — ${fixed} product${fixed === 1 ? "" : "s"} fixed`);
      } else if (review > 0) {
        toast.info(`Repair complete — no auto-fixes, ${review} Smart Special${review === 1 ? "" : "s"} flagged for your eyeball`);
      } else {
        toast.success("Repair complete — everything was already correct");
      }
      // Refresh category rollup so the "Active" badges reflect any fixes.
      await loadCatSpecials();
    } catch (e) {
      const msg = formatErr(e);
      setRepairResult({ error: msg });
      toast.error(msg);
    } finally {
      setRepairBusy(false);
    }
  };

  const toggleCategory = async (row) => {
    if (togglingCat) return;
    const nextEnabled = !row.all_special;
    setTogglingCat(row.category);
    try {
      const { data } = await api.post("/admin/specials/category", {
        category: row.category,
        enabled: nextEnabled,
      });
      const label = CATEGORY_LABELS[row.category];
      if (data.updated === 0) {
        toast.info(`${label} — nothing to update`);
      } else if (nextEnabled) {
        toast.success(`${label} specials activated — ${data.updated} product${data.updated === 1 ? "" : "s"} updated`);
      } else {
        toast.success(`${label} specials deactivated — ${data.updated} product${data.updated === 1 ? "" : "s"} restored`);
      }
      await loadCatSpecials();
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setTogglingCat(null);
    }
  };

  useEffect(() => {
    api.get("/settings").then((r) => {
      setPaused(!!r.data.paused);
      setAnnouncement(r.data.announcement || "");
      setFreeDelivery(!!r.data.free_local_delivery);
    });
  }, []);

  const save = async (next) => {
    setSaving(true);
    try {
      const r = await api.put("/settings", next);
      setPaused(!!r.data.paused);
      setAnnouncement(r.data.announcement || "");
      setFreeDelivery(!!r.data.free_local_delivery);
      toast.success("Settings updated");
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setSaving(false);
    }
  };

  const wipe = async () => {
    setResetBusy(true);
    try {
      const { data } = await api.post("/admin/orders/reset");
      toast.success(`Deleted ${data.deleted} orders`);
      setConfirm(null);
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setResetBusy(false);
    }
  };

  return (
    <div data-testid="admin-settings-page" className="max-w-3xl">
      <h1 className="font-display text-4xl text-[#F5F5DC] mb-10">Settings</h1>

      <div className="bg-[#111] border border-[#2A2A2A] p-8 space-y-8 mb-8">
        {/* Maintenance Mode — large fat-finger pill toggle */}
        <div className="space-y-4">
          <div>
            <p className="font-display text-2xl text-[#F5F5DC] mb-2">Maintenance Mode</p>
            <p className="text-[#A3A3A3] text-sm leading-relaxed max-w-md">
              When ON, the public shop is replaced with a &ldquo;Workshop Closed&rdquo; splash page.
              The admin area stays fully accessible so you can keep working.
            </p>
          </div>
          <button
            type="button"
            onClick={() => save({ maintenance_mode: !paused, announcement })}
            aria-pressed={paused}
            disabled={saving}
            data-testid="maintenance-toggle"
            className={`inline-flex items-center gap-3 h-14 min-h-[56px] px-6 border-2 rounded-full transition-all active:scale-[0.97] touch-manipulation disabled:opacity-60 disabled:cursor-not-allowed ${
              paused
                ? "bg-[#B87333] border-[#B87333] text-white shadow-lg"
                : "bg-[#0A0A0A] border-[#2A2A2A] text-[#F5F5DC] hover:border-[#B87333]"
            }`}
          >
            <Power size={22} strokeWidth={2.4} />
            <span className="font-display text-lg font-bold tracking-wide">
              {paused ? "Maintenance Mode ON" : "Maintenance Mode OFF"}
            </span>
          </button>
          {paused && (
            <p className="text-xs text-[#B87333] uppercase tracking-widest pt-1" data-testid="maintenance-active-hint">
              ⚠ Public visitors currently see the &ldquo;Workshop Closed&rdquo; page
            </p>
          )}
        </div>

        {/* Free Invercargill Delivery — pill toggle, matches Maintenance Mode style */}
        <div className="border-t border-[#2A2A2A] pt-8 space-y-4" data-testid="free-delivery-section">
          <div>
            <p className="font-display text-2xl text-[#F5F5DC] mb-2">Offer Free Invercargill Delivery</p>
            <p className="text-[#A3A3A3] text-sm leading-relaxed max-w-md">
              When ON, local-delivery orders inside Invercargill ship for <strong className="text-[#00BFA5]">$0</strong> instead of the usual $10 fee. Toggle it off anytime to restore the normal rate.
            </p>
          </div>
          <button
            type="button"
            onClick={() => save({ paused, announcement, free_local_delivery: !freeDelivery })}
            aria-pressed={freeDelivery}
            disabled={saving}
            data-testid="free-delivery-toggle"
            className={`inline-flex items-center gap-3 h-14 min-h-[56px] px-6 border-2 rounded-full transition-all active:scale-[0.97] touch-manipulation disabled:opacity-60 disabled:cursor-not-allowed ${
              freeDelivery
                ? "bg-[#00BFA5] border-[#00BFA5] text-white shadow-lg"
                : "bg-[#0A0A0A] border-[#2A2A2A] text-[#F5F5DC] hover:border-[#00BFA5]"
            }`}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
              <rect x="1" y="3" width="15" height="13"/><polygon points="16 8 20 8 23 11 23 16 16 16 16 8"/><circle cx="5.5" cy="18.5" r="2.5"/><circle cx="18.5" cy="18.5" r="2.5"/>
            </svg>
            <span className="font-display text-lg font-bold tracking-wide">
              {freeDelivery ? "Free Delivery ON" : "Free Delivery OFF"}
            </span>
          </button>
          {freeDelivery && (
            <p className="text-xs text-[#00BFA5] uppercase tracking-widest pt-1" data-testid="free-delivery-active-hint">
              ⚑ Local Invercargill delivery is currently FREE at checkout
            </p>
          )}
        </div>

        <div className="border-t border-[#2A2A2A] pt-8">
          <label className="font-display text-2xl text-[#F5F5DC] mb-2 block">Announcement</label>
          <p className="text-[#A3A3A3] text-sm mb-4">Optional message displayed on the maintenance page.</p>
          <textarea
            rows={3}
            className="input-dark"
            value={announcement}
            onChange={(e) => setAnnouncement(e.target.value)}
            placeholder="Back in two weeks — taking time off to fire the kiln…"
            data-testid="announcement-input"
          />
          <button
            onClick={() => save({ paused, announcement })}
            disabled={saving}
            className="btn-primary mt-4"
            data-testid="save-announcement"
          >
            Save
          </button>
        </div>
      </div>

      {/* Manage Specials */}
      <div className="bg-[#111] border border-[#2A2A2A] p-6 sm:p-8 mb-8" data-testid="manage-specials-section">
        <div className="flex items-center gap-3 mb-2">
          <Sparkles className="text-[#B87333]" size={22} />
          <h2 className="font-display text-2xl text-[#F5F5DC]">Manage Specials</h2>
        </div>
        <p className="text-[#A3A3A3] text-sm mb-6 leading-relaxed">
          Activate specials by category — adds <strong className="text-[#B87333]">+$25</strong> to every product in the category
          and enables <strong className="text-[#00BFA5]">free shipping</strong>. Turning OFF restores each piece to exactly what it was before.
        </p>

        {/* ── Repair All Smart Specials ─────────────────────────────────────
             One-click cleanup: scans every product and enforces the invariant
             is_special ⇔ free_shipping ⇔ price bumped by $25. Fixes any
             product currently offering free shipping without the +$25 markup
             (the revenue-leak state). Safe to run repeatedly — idempotent. */}
        <div className="border-2 border-[#B87333]/40 bg-gradient-to-br from-[#B87333]/10 to-transparent rounded-lg p-5 sm:p-6 mb-6" data-testid="repair-specials-panel">
          <div className="flex items-start gap-4 flex-wrap">
            <div className="flex-1 min-w-[220px]">
              <div className="flex items-center gap-2 mb-1">
                <Wrench className="text-[#B87333]" size={18} />
                <p className="font-display text-lg text-[#F5F5DC] font-bold">Repair All Smart Specials</p>
              </div>
              <p className="text-xs sm:text-sm text-[#A3A3A3] leading-relaxed">
                Scans every product. If a piece is offering free shipping without the <strong className="text-[#B87333]">+$25</strong> markup, the markup is added. If a Smart Special was flipped off without restoring the price, it's restored. Safe to run any time — running it on clean data does nothing.
              </p>
            </div>
            <button
              type="button"
              onClick={runSpecialsRepair}
              disabled={repairBusy}
              data-testid="repair-specials-btn"
              className="flex-shrink-0 min-h-[56px] px-6 sm:px-7 inline-flex items-center gap-2 rounded-full font-display font-bold uppercase text-sm sm:text-base tracking-wider transition-all active:scale-[0.97] touch-manipulation disabled:opacity-70 disabled:cursor-wait bg-[#B87333] hover:bg-[#C88a43] text-white shadow-lg shadow-[#B87333]/30 border-2 border-[#B87333]"
            >
              <Wrench size={18} strokeWidth={2.4} />
              {repairBusy ? "Repairing…" : "Repair All"}
            </button>
          </div>

          {repairResult?.error && (
            <div
              className="mt-4 border border-red-700/60 bg-red-950/40 text-red-300 p-3 rounded text-sm"
              data-testid="repair-specials-error"
            >
              Error — {repairResult.error}
            </div>
          )}

          {repairResult?.summary && (
            <div
              className="mt-4 border border-[#B87333]/40 bg-[#0A0A0A] rounded p-4"
              data-testid="repair-specials-result"
            >
              <p className="font-display text-base text-[#F5F5DC] mb-3">
                Repair complete —{" "}
                <span className="text-[#B87333] font-bold">{repairResult.summary.fixed}</span>{" "}
                fixed,{" "}
                {repairResult.summary.needs_review > 0 && (
                  <>
                    <span className="text-yellow-400 font-bold">{repairResult.summary.needs_review}</span>{" "}
                    need review,{" "}
                  </>
                )}
                <span className="text-[#A3A3A3]">{repairResult.summary.without_free_shipping}</span>{" "}
                without free shipping.
              </p>
              {repairResult.summary.fixed > 0 && (
                <ul className="space-y-2 text-xs sm:text-sm mb-3" data-testid="repair-specials-fixed-list">
                  {repairResult.products
                    .filter((p) => p.action.startsWith("FIXED"))
                    .map((p) => (
                      <li key={p.id} className="flex items-start gap-2 text-[#F5F5DC]">
                        <span className="text-[#B87333] font-bold flex-shrink-0">✓</span>
                        <span>
                          <strong>{p.name}</strong> — ${p.before.price?.toFixed(2)} →{" "}
                          <span className="text-[#B87333] font-bold">${p.after.price?.toFixed(2)}</span>
                          <span className="text-[#A3A3A3] block sm:inline sm:ml-2">({p.action.replace("FIXED — ", "")})</span>
                        </span>
                      </li>
                    ))}
                </ul>
              )}
              {repairResult.summary.needs_review > 0 && (
                <div className="mt-3 border border-yellow-600/50 bg-yellow-950/30 rounded p-3" data-testid="repair-specials-review-panel">
                  <p className="text-yellow-300 font-bold text-xs uppercase tracking-widest mb-2">
                    ⚠ Please eyeball these Smart Specials
                  </p>
                  <p className="text-[#A3A3A3] text-xs mb-3 leading-relaxed">
                    Each of these shows a $25 markup on top of a stored baseline, but only you know if the baseline is the price you meant to charge. If any looks wrong (e.g. a board was created with Free Shipping ON and its <em>true</em> base price is higher than the "original" shown here), just open the product, toggle Smart Special OFF, then ON again — that re-anchors it correctly.
                  </p>
                  <ul className="space-y-1.5 text-xs sm:text-sm" data-testid="repair-specials-review-list">
                    {repairResult.products
                      .filter((p) => p.action.startsWith("⚠"))
                      .map((p) => (
                        <li key={p.id} className="flex items-start gap-2 text-[#F5F5DC]">
                          <span className="text-yellow-400 font-bold flex-shrink-0">?</span>
                          <span>
                            <strong>{p.name}</strong> — selling ${p.after.price?.toFixed(2)} (baseline recorded ${p.after.original_price?.toFixed(2)})
                          </span>
                        </li>
                      ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="space-y-3" data-testid="category-specials-list">
          {catSpecials.length === 0 && (
            <p className="text-[#A3A3A3] py-8 text-center text-sm">Loading…</p>
          )}
          {catSpecials.map((row) => {
            const isOn = !!row.all_special;
            const isPartial = !isOn && row.active_count > 0;
            const busy = togglingCat === row.category;
            const label = CATEGORY_LABELS[row.category] || row.category;
            return (
              <div
                key={row.category}
                className={`flex items-center gap-3 sm:gap-4 p-4 sm:p-5 border rounded-lg transition-colors ${
                  isOn ? "bg-[#B87333]/10 border-[#B87333]/50" : "bg-[#0A0A0A] border-[#2A2A2A]"
                }`}
                data-testid={`cat-special-row-${row.category}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-display text-xl text-[#F5F5DC]">{label}</p>
                    {isOn && (
                      <span
                        className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-[#B87333] border border-[#B87333]/60 px-2 py-0.5 font-bold"
                        data-testid={`cat-active-badge-${row.category}`}
                      >
                        <Star size={10} fill="currentColor" strokeWidth={2.4} /> Active
                      </span>
                    )}
                    {isPartial && (
                      <span
                        className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-[#A3A3A3] border border-[#2A2A2A] px-2 py-0.5"
                        data-testid={`cat-partial-badge-${row.category}`}
                      >
                        Partial — {row.active_count} of {row.product_count} on
                      </span>
                    )}
                  </div>
                  <p className="text-[#A3A3A3] text-xs mt-1">
                    {row.product_count} {row.product_count === 1 ? "product" : "products"}
                  </p>
                </div>

                <button
                  type="button"
                  onClick={() => toggleCategory(row)}
                  disabled={busy || row.product_count === 0}
                  aria-pressed={isOn}
                  data-testid={`toggle-cat-${row.category}`}
                  className={`flex-shrink-0 h-14 min-h-[56px] px-5 sm:px-6 inline-flex items-center gap-2 rounded-full border-2 font-bold uppercase text-xs tracking-wider transition-all active:scale-[0.97] touch-manipulation disabled:opacity-60 disabled:cursor-wait ${
                    isOn
                      ? "bg-[#B87333] border-[#B87333] text-white shadow-lg"
                      : "bg-transparent border-[#2A2A2A] text-[#F5F5DC] hover:border-[#B87333]"
                  }`}
                >
                  <Star size={16} fill={isOn ? "currentColor" : "none"} strokeWidth={2.4} />
                  {busy ? "…" : (isOn ? "On" : "Activate")}
                </button>
              </div>
            );
          })}
        </div>
      </div>

      {/* Resin Lab */}
      <div className="bg-[#111] border border-[#2A2A2A] p-6 sm:p-8 mb-8" data-testid="resin-lab-section">
        <h2 className="font-display text-2xl text-[#F5F5DC] mb-2">Resin Lab</h2>
        <p className="text-[#A3A3A3] text-sm mb-6 leading-relaxed">
          When on, the simulator stays visible but new orders are disabled.
        </p>
        <button
          type="button"
          onClick={toggleResinLab}
          disabled={labToggling}
          aria-pressed={labPaused}
          data-testid="resin-lab-toggle"
          className={`inline-flex items-center gap-3 h-14 min-h-[56px] px-6 border-2 rounded-full transition-all active:scale-[0.97] touch-manipulation disabled:opacity-60 ${
            labPaused
              ? "bg-[#B87333] border-[#B87333] text-white shadow-lg"
              : "bg-[#0A0A0A] border-[#2A2A2A] text-[#F5F5DC] hover:border-[#B87333]"
          }`}
        >
          <Power size={22} strokeWidth={2.4} />
          <span className="font-display text-lg font-bold tracking-wide">
            {labPaused ? "Resin Lab Paused" : "Pause Resin Lab"}
          </span>
        </button>
        {labPaused && (
          <p className="text-xs text-[#B87333] uppercase tracking-widest pt-3">
            ⚠ Simulator visible; &ldquo;Build This For Me&rdquo; disabled on /resin-lab
          </p>
        )}
      </div>

      {/* Manage Auctions */}
      <div className="bg-[#111] border border-[#2A2A2A] p-6 sm:p-8 mb-8" data-testid="auctions-section">
        <div className="flex items-center gap-3 mb-2">
          <Gavel className="text-[#B87333]" size={20} />
          <h2 className="font-display text-2xl text-[#F5F5DC]">Manage Auctions</h2>
        </div>
        <p className="text-[#A3A3A3] text-sm mb-6">
          Pick a product and start a 15-day sealed-clock auction on /auction. Winner is emailed automatically at close.
        </p>

        {(() => {
          const active = auctions.find((a) => a.status === "active");
          if (active) {
            return (
              <div className="border-2 border-[#B87333] bg-[#B87333]/10 p-5 mb-4" data-testid="active-auction-card">
                <p className="smallcaps !text-[#B87333] mb-2">Active — {active.product_name}</p>
                <div className="grid sm:grid-cols-3 gap-4">
                  <div>
                    <p className="text-[10px] uppercase tracking-widest text-[#A3A3A3]">Current bid</p>
                    <p className="font-display font-black text-3xl !text-white" data-testid="active-current-bid">${Number(active.current_highest_bid).toFixed(0)}</p>
                  </div>
                  <div>
                    <p className="text-[10px] uppercase tracking-widest text-[#A3A3A3] flex items-center gap-1"><Clock size={10} /> Time left</p>
                    <p className="font-display font-black text-3xl !text-white" data-testid="active-time-left">{formatTimeLeft(active.end_time)}</p>
                  </div>
                  <div>
                    <p className="text-[10px] uppercase tracking-widest text-[#A3A3A3]">Bids</p>
                    <p className="font-display font-black text-3xl !text-white">{(active.bids || []).length}</p>
                  </div>
                </div>
                {active.highest_bidder_name && (
                  <p className="text-xs text-[#A3A3A3] mt-4">
                    Leader: <span className="text-[#F5F5DC]">{active.highest_bidder_name}</span>
                    {" · "}{active.highest_bidder_email}
                    {" · "}{active.highest_bidder_phone}
                  </p>
                )}
                <button onClick={() => endAuction(active.id)} disabled={auctionBusy}
                  data-testid="end-auction-btn"
                  className="mt-4 inline-flex items-center gap-2 h-10 px-4 text-xs uppercase tracking-widest border-2 border-red-500 text-red-400 hover:bg-red-500 hover:text-white transition-colors">
                  <StopCircle size={14} /> End Auction Now
                </button>
              </div>
            );
          }
          return (
            <div className="space-y-3 mb-4">
              <select className="input-dark min-h-[48px] w-full" data-testid="auction-product-select"
                value={auctionProductId} onChange={(e) => setAuctionProductId(e.target.value)}>
                <option value="">Choose a product…</option>
                {products.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name} — ${Number(p.price).toFixed(0)}
                  </option>
                ))}
              </select>
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <input
                    type="number"
                    min="1"
                    step="1"
                    placeholder="Starting Bid Price"
                    className="input-dark min-h-[48px] w-full font-bold"
                    data-testid="auction-starting-bid-input"
                    value={auctionStartingBid}
                    onChange={(e) => setAuctionStartingBid(e.target.value)}
                  />
                  <p className="text-[10px] uppercase tracking-widest text-[#757575] mt-1">
                    Amount to beat with the first bid. Defaults to the product&apos;s shop price if left blank.
                  </p>
                </div>
                <div>
                  <input
                    type="number"
                    min="1"
                    step="1"
                    placeholder="Buy It Now price (optional)"
                    className="input-dark min-h-[48px] w-full"
                    data-testid="auction-buy-now-input"
                    value={auctionBuyNow}
                    onChange={(e) => setAuctionBuyNow(e.target.value)}
                  />
                  <p className="text-[10px] uppercase tracking-widest text-[#757575] mt-1">
                    If set, customers can skip bidding and buy instantly at this price (+ $25 shipping)
                  </p>
                </div>
              </div>
              <div>
                <textarea
                  rows={4}
                  placeholder="Auction Story / Advertisement (optional) — tell the story of this piece: reclaimed timber origin, size, standout grain, why it's special…"
                  className="input-dark w-full leading-relaxed"
                  data-testid="auction-description-input"
                  value={auctionDescription}
                  onChange={(e) => setAuctionDescription(e.target.value)}
                />
                <p className="text-[10px] uppercase tracking-widest text-[#757575] mt-1">
                  Appears prominently on the auction page. Leave blank to use the product&apos;s default description.
                </p>
              </div>
              <button onClick={startAuction} disabled={!auctionProductId || auctionBusy}
                data-testid="start-auction-btn"
                className="inline-flex items-center justify-center gap-2 h-12 px-5 bg-[#B87333] text-white font-bold uppercase tracking-wider text-xs hover:bg-[#A6632B] transition-colors disabled:opacity-50">
                <Play size={14} /> Start 15-Day Auction
              </button>
            </div>
          );
        })()}

        {auctions.filter((a) => a.status === "ended").length > 0 && (
          <div className="mt-6">
            <p className="smallcaps mb-3">Past Auctions</p>
            <ul className="space-y-2 text-sm">
              {auctions.filter((a) => a.status === "ended").slice(0, 10).map((a) => (
                <li key={a.id} className="flex justify-between border-b border-[#1a1a1a] pb-1.5" data-testid={`past-auction-${a.id}`}>
                  <span className="text-[#A3A3A3]">{a.product_name} · {a.highest_bidder_name || "no bids"}</span>
                  <span className="font-display font-bold text-[#F5F5DC]">${Number(a.current_highest_bid).toFixed(0)}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {/* Danger Zone */}
      <div className="bg-[#0E0506] border border-red-900/50 p-8" data-testid="danger-zone">
        <div className="flex items-center gap-3 mb-2">
          <AlertTriangle className="text-red-400" size={20} />
          <h2 className="font-display text-2xl text-red-300">Danger Zone</h2>
        </div>
        <p className="text-[#A3A3A3] text-sm mb-6 leading-relaxed">
          Permanent reset tools. Use these to wipe test data before launch or to start the catalog over.
          Both actions are irreversible — orders are removed from the database (Stripe receipts and customer emails are NOT deleted from Stripe).
        </p>

        <div className="space-y-3">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <p className="text-[#F5F5DC]">Clear all orders</p>
              <p className="text-[#A3A3A3] text-xs mt-1">Removes every order record from the database. Stripe records are unaffected.</p>
            </div>
            <DangerButton
              label="Clear All Orders"
              testid="clear-orders-btn"
              busy={resetBusy && confirm === "orders"}
              onClick={() => setConfirm("orders")}
            />
          </div>
          {confirm === "orders" && (
            <ConfirmCard
              testid="confirm-orders"
              title="This will permanently delete ALL orders."
              body="This cannot be undone. Are you sure?"
              busy={resetBusy}
              onCancel={() => setConfirm(null)}
              onConfirm={wipe}
              confirmLabel="Yes, delete all orders"
            />
          )}
        </div>
      </div>
    </div>
  );
}
